import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { NbDialogService, NbPopoverDirective } from '@nebular/theme';
import { debounce, castArray, isEmpty } from 'lodash';
import { AuthorData } from '../../../@core/interfaces/author';
import { EntityService } from '../../../@core/utils/entity.service';
import { ensureArray } from '../../../@core/utils/utils.service';

@Component({
  selector: 'ch-relation-selection',
  templateUrl: './relation-selection.component.html',
  styleUrls: ['./relation-selection.component.scss'],
})
export class RelationSelectionComponent implements OnInit, OnChanges {
  dialogRef: any = null;

  @Input() entityName: string; // NEW: name of the entity/relation (e.g. "Contact", "Client", "Author")
  @Input() includeRelation: boolean = true;
  @Input() isEditable: boolean = false;
  // selectedRecords input may be an array or a single object; coerce internally in ngOnChanges
  @Input() selectedRecords: any = null;
  // emit either an array (multiSelect) or a single object/null (single-select)
  @Output() selectedRecordsChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() multiSelect: boolean = false; // when true allow multiple selection, otherwise single-select

  @Input() allRecords: any[] = [];
  @Input() type: string; // for label, can be set to entityName
  @Input() emptyResultsMsg = "No records found.";
  @Input() generateLinks: boolean = false;
  // Declarative Strapi filters always applied when fetching candidates for this relation
  @Input() filters: { [field: string]: any } | null = null;

  options: any[] = [];
  filteredOptions$: Observable<any[]>;
  loadedRecords: any[] = [];
  // Stable candidate list for the current search. Kept separate from `allRecords`:
  // that's an @Input, so Angular overwrites it directly from the template binding on
  // every change-detection cycle (e.g. `[allRecords]="field.options || []"`), independent
  // of anything done here.
  candidateRecords: any[] = [];

  chosenRecord: any = null;
  chosenRecordRelation: any;
  debouncedSearch: any;

  @ViewChild('autoInput') input;
  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective;

  isOpen = true;
  statusIndicator = "Begin typing to search";
  isLoading = true;
  constructor(
    private dialog: NbDialogService,
    private cdRef: ChangeDetectorRef,
    private authorService: AuthorData, 
    private entityDataService: EntityService,
    // You may want to inject a generic service or use a service map for different entities
    // For now, you can inject all possible services and select based on entityName
    // Example:
    // private contactService: ContactsService,
    // private authorService: AuthorService,
    // etc.
  ) { }

  ngOnInit(): void {
    this.loadAllRecords();
    this.filteredOptions$ = of(this.loadedRecords);

    this.debouncedSearch = debounce((query: string) => this.performSearch(query), 750);

    // setTimeout(() => this.popover?.show(), 3000);
  }

  loadAllRecords() {
    this.performSearch('');
  }

  /** Performs a real server-side query: merges the declarative `filters` with a
   * `$containsi` search across the entity's searchFields (empty query = no search term). */
  private performSearch(query: string): void {
    const service = this.entityDataService.getEntityDataByName(this.entityName);
    if (!service || !service.listAll) return;

    this.isLoading = true;

    const searchFilters = this.buildSearchFilters(query);
    const mergedFilters = { ...(this.filters ?? {}), ...(searchFilters ?? {}) };

    service.listAll(Object.keys(mergedFilters).length ? mergedFilters : undefined).subscribe((records: any) => {
      const arr = ensureArray(records);
      this.candidateRecords = arr;
      this.allRecords = arr;
      this.loadedRecords = arr;
      this.isLoading = false;
      this.filteredOptions$ = of(arr);
      this.isOpen = true;
      this.statusIndicator = arr.length ? "Loaded. Click field to show results." : "No matches found.";
      this.cdRef.markForCheck();
    });
  }

  /** Builds an `{ $and: [{ $or: [...] }] }` filter: each word of the query must match at
   * least one configured searchField (or titleField fallback), so multi-word queries like
   * "Jamal Solomon" match records with the words split across firstName/lastName. */
  private buildSearchFilters(query: string): { [field: string]: any } | null {
    const trimmed = (query ?? '').trim();
    if (!trimmed) return null;

    const entityConfig = this.entityDataService.getEntityConfigByName(this.entityName);
    const searchFields: string[] = entityConfig?.meta?.searchFields
      ?? (entityConfig?.meta?.titleField ? [entityConfig.meta.titleField] : []);
    if (!searchFields.length) return null;

    const words = trimmed.split(/\s+/);
    return { $and: words.map(word => ({ $or: searchFields.map(field => ({ [field]: { $containsi: word } })) })) };
  }
  
  addRecord(event: any): void {
    if (!this.chosenRecord) {
      this.statusIndicator = "Please click the field below and select a listed name first.";
      this.popover.show();
      return;
    }

    if (this.dialogRef) {
      this.dialogRef.close(event);
      this.dialogRef = null;
    }

    if (this.multiSelect) {
      const exists = this.selectedRecords.some(r => r?.id === this.chosenRecord?.id);
      if (!exists) {
        this.selectedRecords = [...this.selectedRecords, this.chosenRecord];
      }
      this.selectedRecordsChange.emit(this.selectedRecords);
    } else {
      this.selectedRecords = [this.chosenRecord];
      // emit single object for single-select
      this.selectedRecordsChange.emit(this.chosenRecord);
    }
  }

  search = (query: string) => {
    this.performSearch(query);
  }

  spawnDialog(dialogTemplate: TemplateRef<any>): void {
    this.dialogRef = this.dialog.open(dialogTemplate, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      dialogClass: "add-relation-dialog"
    });
    this.chosenRecord = null;
    this.chosenRecordRelation = null;
  }

  dismissDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close(null);
      this.dialogRef = null;
    }
  }

  removeRecord(record: any): void {
    if (this.multiSelect) {
      this.selectedRecords = this.selectedRecords.filter(r => {
        if (r?.id !== undefined && record?.id !== undefined) return r.id !== record.id;
        return r !== record;
      });
      this.selectedRecordsChange.emit(this.selectedRecords);
    } else {
      this.selectedRecords = [];
      // single-select removal emits null
      this.selectedRecordsChange.emit(null);
    }
  }

  popout(_: any, item: any): void {
    if (this.isEditable !== true && this.generateLinks === true) {
      window.open(`/pages/${this.entityName?.toLowerCase()}s/${item.entity?.id}/view`);
    }
  }

  onChange() {
    this.statusIndicator = "Searching...";
    this.popover.show();
    this.debouncedSearch(this.input.nativeElement.value);
  }

  // nb-option's [value] is bound to the full record, so selectedChange gives us the
  // record directly - no need to re-search candidateRecords by display text.
  onSelectionChange(record: any) {
    this.chosenRecord = record ?? null;
    this.popover.hide();

    // If not in dialog (inline usage), apply selection immediately
    if (!this.dialogRef) {
      if (this.multiSelect) {
        if (this.chosenRecord) {
          const exists = this.selectedRecords.some(r => r?.id === this.chosenRecord?.id);
          if (!exists) {
            this.selectedRecords = [...this.selectedRecords, this.chosenRecord];
          }
        }
        this.selectedRecordsChange.emit(this.selectedRecords);
      } else {
        this.selectedRecords = this.chosenRecord ? [this.chosenRecord] : [];
        // emit single object (or null) for single-select
        this.selectedRecordsChange.emit(this.chosenRecord ?? null);
      }
    }
  }

  setRelation(event) {
    this.chosenRecordRelation = event.target.value;
  }

  private coerceToArray<T>(v: T | T[] | null | undefined): T[] {
    // castArray(null) -> [null], so filter out null/undefined
    return castArray(v).filter(x => x !== null && x !== undefined) as T[];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.entityName && !changes.entityName.firstChange) {
      this.loadAllRecords();
    }

    if (changes.filters && !changes.filters.firstChange) {
      this.loadAllRecords();
    }

    if (changes.allRecords) {
      const newAll = this.coerceToArray(changes.allRecords.currentValue);
      this.allRecords = newAll;
      // Only adopt the externally-provided list when nothing drives our own fetch -
      // otherwise the empty array literal templates re-create every change-detection
      // cycle would wipe out records performSearch() just fetched.
      if (!this.entityName && newAll.length > 0) {
        this.candidateRecords = newAll;
        this.loadedRecords = newAll;
        this.filteredOptions$ = of(newAll);
      }
    }

    if (changes.selectedRecords && changes.selectedRecords.currentValue !== undefined) {
      // accept either single object or array input
      const incoming = changes.selectedRecords.currentValue;
      const arr = Array.isArray(incoming) ? incoming : (isEmpty(incoming) ? [] : [incoming]);
      if (this.multiSelect) {
        this.selectedRecords = arr;
      } else {
        this.selectedRecords = arr.length > 0 ? [arr[0]] : [];
      }
    }
  }

  // Helper to get display name for any entity
  getDisplayName(entity: any): string {
    if (!entity) return '';
    if (entity?.firstName && entity?.lastName) return `${entity.firstName} ${entity.lastName}`;
    if (entity?.name) return entity.name;
    if (entity?.title) return entity.title;
    return entity.id || '';
  }
}